import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import Validator from 'mixins/validator';
import Resetter from 'mixins/resetter';
import { connect } from 'react-redux';
import { Button, IconButton } from 'react-toolbox/lib/button';
import { Dialog } from 'react-toolbox';
import styles from 'styles/input.scss';
import StyledSelect from 'components/StyledSelect';
import ConfirmDialog from 'components/ConfirmDialog';
import TextArea from 'components/TextArea';

import S from 'modal.scss';
import { cancelPhase, clearValidation } from 'actionCreators/phase';
import PhaseType from 'types/phase';
import { getPhaseErrors } from '../../../../../../reducers';
import { error, errorSummary, vendorDisplayName, vendorLowes } from '../../../../../../utils';
import { RAILS_DATE } from 'config/constants';

const CANCEL_MESSAGE = 'Are you sure you want to cancel?';

class CancelPhaseModal extends Resetter(Validator(React.Component)) {
  static DEFAULT_STATE = {
    who: '',
    reason: '',
    showConfirm: false,
    errors: {},
  }
  static VALIDATIONS = { who: true, reason: true }

  handleWhoChange = e => this.setState({ who: e });

  handleReasonChange = e => this.setState({ reason: e.target.value });

  handleSubmit = () => {
    // We need to run local validation because the client will always send a
    // note and a name to the API, which are the only two required params. `who`
    // and `reason` are used to compose the note, so they need to be checked by
    // the client. This allows the same API endpoint to be used for multiple
    // actions.
    if (!this.validate()) return;
    this.setState({ showConfirm: true });
  }

  handleClose() {
    super.handleClose();
    this.props.clearValidation();
  }

  handleConfirmSubmit = () => {
    if (!this.validate()) return;
    const phase = this.props.phase;
    const who = this.state.who;
    const reason = this.state.reason;
    const phaseProps = {
      event: {
        name: 'Canceled',
        kind: 'phase_canceled',
        data: {
          who,
          from_status: phase.status_name,
          note: reason,
        },
      },
    };
    this.props.cancelPhase(phase.id, phaseProps).then(() => {
      this.handleClose();
    }).catch(() => { this.setState({ showConfirm: false }); });
  }

  handleConfirmClose = () => this.handleClose();

  // Until we make PFT its own vendor, we have to make special consideration
  // TODO: Remove this function when PFT is a vendor
  options = () => {
    const { phase: { source, workroom: { name } } } = this.props;
    const vendorOption = vendorDisplayName(source, name);
    const isVendorLowes = vendorLowes(source);
    const baseOptions = ['Customer',
      ...(source === 'IFI' ? [] : ['SPIG']),
      'Duplicate',
      'Contractor',
      'Out of Scope',
      'Customer Not Requested',
      vendorOption];
    if (vendorOption !== 'Lowe\'s' && isVendorLowes) baseOptions.push('Lowe\'s');
    return (vendorOption !== 'Refloor' ?
      baseOptions :
      [...baseOptions, vendorDisplayName('PFT', null, true)])
      .map(o => ({ value: o, label: o }));
  }

  futureApptExists = () =>
    (this.props.phase.appointments || []).some(appt =>
      moment(appt.start_date, RAILS_DATE).isAfter(moment()));

  determineCancelMessage = () => (this.futureApptExists() ?
    `${CANCEL_MESSAGE} There are future appointments scheduled for this phase.` :
    `${CANCEL_MESSAGE} Please confirm there are no outstanding exceptions open on L/P & Accounting.`);

  render = () => {
    const { active, serverErrors } = this.props;
    const { who, reason, showConfirm, errors } = this.state;
    return (<Dialog
      active={active}
      className={S.modal}
      type="fullscreen"
      onEscKeyDown={this.handleClose}
      onOverlayClick={this.handleClose}
    >
      <div className={styles.errored}>
        {Object.keys(serverErrors).length > 0 &&
          <span className={styles.error}>{errorSummary(serverErrors)}</span>}
      </div>

      <IconButton
        icon="close"
        className={S.close}
        onClick={this.handleClose}
        tabIndex={-1}
      />

      <h5 className={S.title}>Cancel Phase</h5>

      <StyledSelect
        className={S.select}
        label="Who canceled? *"
        onChange={this.handleWhoChange}
        options={this.options()}
        value={who}
        placeholder={'Select'}
        error={error('who', errors)}
      />

      <TextArea
        label="Reason *"
        value={reason}
        errors={errors.reason}
        onChange={this.handleReasonChange}
      />

      <Button
        label="Cancel Phase"
        flat inverse ripple
        className={S.submit}
        onClick={this.handleSubmit}
      />

      <ConfirmDialog
        title={this.determineCancelMessage()}
        okLabel="Yes, Cancel the Phase"
        cancelLabel="No"
        show={showConfirm}
        cancel={this.handleConfirmClose}
        dismiss={this.handleConfirmClose}
        proceed={this.handleConfirmSubmit}
      />
    </Dialog>);
  }
}

CancelPhaseModal.propTypes = {
  active: PropTypes.bool.isRequired,
  cancelPhase: PropTypes.func.isRequired,
  clearValidation: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  serverErrors: PropTypes.shape({}),
};

CancelPhaseModal.defaultProps = { active: false };

export default connect(
  state => ({ serverErrors: getPhaseErrors(state) }),
  { cancelPhase, clearValidation },
)(CancelPhaseModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/cancelPhaseModal.jsx